import { Button, Modal, Spinner } from "@scoach/ui";
import { useRef, useState } from "react";

import { streamSse } from "../../../lib/http.ts";
import { Markdown } from "../../companies/Markdown.tsx";

type Turn = { role: "user" | "model"; text: string };

export interface BulkChatMeetingRef {
  id: string;
  client: string;
  title: string;
}

const SUGGESTED = [
  "Summarize the open action items across these meetings.",
  "What objections or concerns came up most often?",
  "Which meetings mentioned pricing or budget?",
  "What are the next steps for each meeting?",
];

/**
 * "Ask about selected meetings" — free-text Q&A grounded in several meetings the rep
 * picked on the dashboard. The rep can narrow which of the selected meetings to
 * include. Streams from `/meetings/bulk-chat/stream` (server-side Vertex, no personal
 * key). In-memory only (not persisted).
 *
 * Label mode: when `labels` is provided, the scope is "all your meetings tagged with
 * these labels" (resolved server-side, capped at the 5 most recent). The passed
 * `meetings` are shown read-only as a preview of what matched.
 */
export function BulkChatModal({
  meetings,
  labels,
  onClose,
}: {
  meetings: BulkChatMeetingRef[];
  labels?: string[];
  onClose: () => void;
}) {
  const labelMode = !!labels && labels.length > 0;
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const [included, setIncluded] = useState<Set<string>>(new Set(meetings.map((m) => m.id)));
  const scrollRef = useRef<HTMLDivElement>(null);

  // In label mode the scope is the labels (server-resolved); otherwise it's the ticked
  // meetings. `canAsk` gates the input either way.
  const includedIds = meetings.map((m) => m.id).filter((id) => included.has(id));
  const capped = !labelMode && includedIds.length > 5;
  const canAsk = labelMode || includedIds.length > 0;

  function toggle(id: string) {
    setIncluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function ask(q?: string) {
    const question = (q ?? input).trim();
    if (!question || streaming || !canAsk) return;
    setError("");
    setInput("");
    const history = turns.map((t) => ({ role: t.role, text: t.text }));
    setTurns((prev) => [...prev, { role: "user", text: question }, { role: "model", text: "" }]);
    setStreaming(true);
    try {
      await streamSse(
        "/meetings/bulk-chat/stream",
        labelMode
          ? { meetingIds: [], labels, question, history }
          : { meetingIds: includedIds.slice(0, 5), question, history },
        {
          onDelta: (delta) => {
            setTurns((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === "model")
                next[next.length - 1] = { ...last, text: last.text + delta };
              return next;
            });
            scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
          },
        },
      );
    } catch (e) {
      setError((e as Error).message || "Couldn't answer that. Try again.");
      setTurns((prev) => {
        const last = prev[prev.length - 1];
        return last && last.role === "model" && !last.text ? prev.slice(0, -1) : prev;
      });
    } finally {
      setStreaming(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={labelMode ? "Ask about meetings by label" : "Ask about your meetings"}
      width={760}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          flex: 1,
          minHeight: 0,
          padding: "0 16px 16px",
        }}
      >
        {/* Scope: label chips (read-only, label mode) OR toggleable meeting chips. */}
        <div style={{ flex: "0 0 auto" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-3)", marginBottom: 6 }}>
            {labelMode
              ? `Meetings tagged ${labels?.map((l) => `“${l}”`).join(", ")}`
              : `Meetings in scope (${includedIds.length})`}
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              maxHeight: 96,
              overflowY: "auto",
            }}
          >
            {meetings.map((m) => {
              const on = included.has(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  className={`bulk-mtg-chip${!labelMode && on ? " on" : ""}`}
                  onClick={() => !labelMode && toggle(m.id)}
                  disabled={labelMode}
                  style={labelMode ? { cursor: "default" } : undefined}
                  title={m.client ? `${m.title} · ${m.client}` : m.title}
                >
                  {!labelMode && <span className="bulk-mtg-check">{on ? "✓" : "+"}</span>}
                  {m.title || m.client || "Untitled meeting"}
                </button>
              );
            })}
          </div>
          {labelMode && (
            <div style={{ fontSize: 12, color: "var(--text-4)", marginTop: 6 }}>
              SellSynq uses your 5 most recent meetings with{" "}
              {labels && labels.length > 1 ? "any of these labels" : "this label"} (transcripts are
              large).
            </div>
          )}
          {capped && (
            <div style={{ fontSize: 12, color: "var(--text-4)", marginTop: 6 }}>
              Using the first 5 in-scope meetings (transcripts are large).
            </div>
          )}
        </div>

        <div
          ref={scrollRef}
          style={{
            overflowY: "auto",
            flex: 1,
            minHeight: 140,
            border: "1px solid var(--border-soft)",
            borderRadius: 10,
            padding: "10px 12px",
          }}
        >
          {turns.length === 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "6px 0" }}>
              <div style={{ fontSize: 13, color: "var(--text-3)" }}>
                Ask a question across the meetings in scope, or try one of these:
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {SUGGESTED.map((q) => (
                  <button
                    key={q}
                    type="button"
                    className="bulk-suggest-chip"
                    onClick={() => void ask(q)}
                    disabled={streaming || !canAsk}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {turns.map((t, i) =>
                t.role === "user" ? (
                  // biome-ignore lint/suspicious/noArrayIndexKey: append-only chat log
                  <div key={i} style={{ alignSelf: "flex-end", maxWidth: "85%" }}>
                    <div
                      style={{
                        background: "var(--gc-blue-50, #e8f0fe)",
                        color: "var(--text-1)",
                        borderRadius: 10,
                        padding: "8px 12px",
                        fontSize: 13,
                      }}
                    >
                      {t.text}
                    </div>
                  </div>
                ) : (
                  // biome-ignore lint/suspicious/noArrayIndexKey: append-only chat log
                  <div key={i} style={{ alignSelf: "flex-start", maxWidth: "94%" }}>
                    {t.text ? (
                      <Markdown>{t.text}</Markdown>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          color: "var(--text-3)",
                        }}
                      >
                        <Spinner size={14} /> Thinking…
                      </div>
                    )}
                  </div>
                ),
              )}
            </div>
          )}
          {error && (
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--danger, #d93025)" }}>
              {error}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
          <input
            type="text"
            className="setup-input"
            style={{ flex: 1 }}
            value={input}
            placeholder={
              !canAsk
                ? "Select at least one meeting above…"
                : labelMode
                  ? "Ask a question across meetings with these labels…"
                  : "Ask a question across the selected meetings…"
            }
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void ask();
              }
            }}
            disabled={streaming || !canAsk}
            // biome-ignore lint/a11y/noAutofocus: the input is the primary control of this modal
            autoFocus
          />
          <Button onClick={() => void ask()} disabled={streaming || !input.trim() || !canAsk}>
            {streaming ? "…" : "Ask"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
